/**
 * TasksByJourneyView Component
 * Displays tasks grouped by new hire journey
 */

import { useMemo } from "react";
import { Avatar, Box, HStack, VStack } from "@chakra-ui/react";
import CDS from "design-system";
import { useNavigate } from "react-router";
import moment from "moment-timezone";
import { Task } from "features/Resources/Task/models";
import { isTaskOpen } from "features/Resources/Task/helpers";
import { TaskStatus, CLOSED_TASK_STATUSES } from "features/Resources/Task/constants";
import { useQueries } from "@tanstack/react-query";
import { newHireJourneyQueryArgs, useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import TaskCard from "./TaskCard";

interface JourneyGroup {
  journeyId: string;
  tasks: Task[];
}

interface TasksByJourneyViewProps {
  tasks: Task[];
  onTaskClick?: (task: Task) => void;
  sortBy?: string;
  currentUserId?: string;
}

// Anchor "today" in the new hire's timezone so the day count flips at their
// midnight rather than the viewer's browser midnight — matters for managers
// and people-team users in different timezones than the new hire.
function computeDayLabel(startDate?: Date | string, timezone?: string): string {
  if (!startDate) return "";
  const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const start = moment.tz(String(startDate), tz).startOf("day");
  const today = moment.tz(tz).startOf("day");
  const diff = today.diff(start, "days");
  if (diff < 0) return `Starts in ${Math.abs(diff)} days`;
  if (diff === 0) return "Day 1";
  return `Day ${diff + 1}`;
}

function JourneyHeader({ journeyId, currentUserId }: { journeyId: string; currentUserId?: string }) {
  const navigate = useNavigate();
  const { data: journey } = useNewHireJourneyDetailQuery(journeyId);
  const newHire = journey?.newHire;
  const dayLabel = computeDayLabel(journey?.startDate, newHire?.timezone);
  const isOwnJourney = !!(currentUserId && journey?.newHireId === currentUserId);

  return (
    <HStack justify="space-between" align="center" px="2px">
      <HStack spacing={2} align="center">
        <Avatar size="xs" name={newHire?.fullName} src={newHire?.avatarUrl} boxSize="22px" fontSize="8px" />
        <CDS.Text14
          fontSize="13px"
          fontWeight="500"
          color="gray.800"
          cursor={isOwnJourney ? "default" : "pointer"}
          _hover={isOwnJourney ? {} : { color: "brand.500" }}
          onClick={isOwnJourney ? undefined : () => navigate(`/new-hires/${journeyId}`)}
        >
          {isOwnJourney ? "My Onboarding" : newHire?.fullName || "Loading..."}
        </CDS.Text14>
        {dayLabel && (
          <CDS.Text11 color="gray.400">
            · {dayLabel}
          </CDS.Text11>
        )}
      </HStack>
      <CDS.Text12
        color="brand.500"
        cursor="pointer"
        _hover={{ textDecoration: "underline" }}
        onClick={() => navigate(isOwnJourney ? "/my-onboarding" : `/new-hires/${journeyId}/tasks`)}
      >
        {isOwnJourney ? "Go to Onboarding Dashboard →" : "View journey →"}
      </CDS.Text12>
    </HStack>
  );
}

function JourneySection({
  journeyId,
  tasks,
  onTaskClick,
  currentUserId,
}: {
  journeyId: string;
  tasks: Task[];
  onTaskClick?: (task: Task) => void;
  currentUserId?: string;
}) {
  const sortedTasks = useMemo(() => {
    const sortByDueDate = (a: Task, b: Task) => {
      if (!a.dueAt) return 1;
      if (!b.dueAt) return -1;
      return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
    };

    const nonUpcoming = tasks.filter((t) => t.status !== TaskStatus.UPCOMING);
    const openTasks = nonUpcoming.filter(isTaskOpen).sort(sortByDueDate);
    const closedTasks = nonUpcoming
      .filter((t) => (CLOSED_TASK_STATUSES as readonly string[]).includes(t.status))
      .sort(sortByDueDate);

    return [...openTasks, ...closedTasks];
  }, [tasks]);

  return (
    <Box>
      <Box mb={2}>
        <JourneyHeader journeyId={journeyId} currentUserId={currentUserId} />
      </Box>
      <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden">
        {sortedTasks.length > 0 ? (
          sortedTasks.map((task) => (
            <TaskCard key={task.id} task={task} compact showAssignee={false} onClick={onTaskClick} />
          ))
        ) : (
          <CDS.Text14 fontSize="13px" color="gray.400" textAlign="center" py={5}>
            All tasks complete
          </CDS.Text14>
        )}
      </Box>
    </Box>
  );
}

export function SummaryBar({ tasks }: { tasks: Task[] }) {
  const nonUpcoming = tasks.filter((t) => t.status !== TaskStatus.UPCOMING);
  const openCount = nonUpcoming.filter(isTaskOpen).length;
  const overdueCount = nonUpcoming.filter((t) => t.isOverdue).length;

  if (openCount === 0 && overdueCount === 0) return null;

  return (
    <HStack spacing={4}>
      {openCount > 0 && (
        <HStack spacing="6px" fontSize="13px" color="gray.600">
          <Box w="7px" h="7px" borderRadius="full" bg="gray.300" />
          <CDS.Text14 fontSize="13px" fontWeight="500" color="gray.800">
            {openCount}
          </CDS.Text14>
          <CDS.Text14 fontSize="13px">open</CDS.Text14>
        </HStack>
      )}
      {overdueCount > 0 && (
        <HStack spacing="6px" fontSize="13px" color="gray.600">
          <Box w="7px" h="7px" borderRadius="full" bg="red.400" />
          <CDS.Text14 fontSize="13px" fontWeight="500" color="gray.800">
            {overdueCount}
          </CDS.Text14>
          <CDS.Text14 fontSize="13px">overdue</CDS.Text14>
        </HStack>
      )}
    </HStack>
  );
}

function getEarliestStartDate(tasks: Task[]): number {
  const dates = tasks
    .map((t) => (t.effectiveStartDate ? new Date(t.effectiveStartDate).getTime() : null))
    .filter((d): d is number => d !== null);
  return dates.length > 0 ? Math.min(...dates) : Infinity;
}

function sortJourneyGroups(groups: JourneyGroup[], sortBy: string): JourneyGroup[] {
  switch (sortBy) {
    case "new_hire_start_desc":
      return [...groups].sort((a, b) => getEarliestStartDate(b.tasks) - getEarliestStartDate(a.tasks));
    case "new_hire_start_asc":
      return [...groups].sort((a, b) => getEarliestStartDate(a.tasks) - getEarliestStartDate(b.tasks));
    case "most_open":
    default:
      return [...groups].sort((a, b) => {
        const aOpen = a.tasks.filter(isTaskOpen).length;
        const bOpen = b.tasks.filter(isTaskOpen).length;
        return bOpen - aOpen;
      });
  }
}

export default function TasksByJourneyView({
  tasks,
  onTaskClick,
  sortBy = "most_open",
  currentUserId,
}: TasksByJourneyViewProps) {
  const baseGroups = useMemo(() => {
    const groupMap = new Map<string, Task[]>();

    tasks.forEach((task) => {
      const jId = task.newHireJourneyId || "unassigned";
      const existing = groupMap.get(jId);
      if (existing) {
        existing.push(task);
      } else {
        groupMap.set(jId, [task]);
      }
    });

    return Array.from(groupMap.entries()).map(
      ([journeyId, journeyTasks]): JourneyGroup => ({ journeyId, tasks: journeyTasks }),
    );
  }, [tasks]);

  // Fetch journey details for all groups to detect own journey
  const journeyIds = useMemo(() => baseGroups.map((g) => g.journeyId), [baseGroups]);
  const journeyQueries = useQueries({
    queries: journeyIds.map((id) => newHireJourneyQueryArgs(id)),
  });

  // Build ownership map and sort: own journey always first, then apply sortBy
  const journeyGroups = useMemo(() => {
    const ownJourneyIds = new Set<string>();
    if (currentUserId) {
      journeyQueries.forEach((q, i) => {
        if (q.data?.newHireId === currentUserId) {
          ownJourneyIds.add(journeyIds[i]);
        }
      });
    }

    const ownGroups = baseGroups.filter((g) => ownJourneyIds.has(g.journeyId));
    const otherGroups = baseGroups.filter((g) => !ownJourneyIds.has(g.journeyId));

    return [...ownGroups, ...sortJourneyGroups(otherGroups, sortBy)];
  }, [baseGroups, journeyQueries, currentUserId, journeyIds, sortBy]);

  return (
    <VStack spacing={4} align="stretch" w="100%">
      {journeyGroups.map((group) => (
        <JourneySection
          key={group.journeyId}
          journeyId={group.journeyId}
          tasks={group.tasks}
          onTaskClick={onTaskClick}
          currentUserId={currentUserId}
        />
      ))}
    </VStack>
  );
}
